import { FC, useState, useEffect } from 'react';
import { Page } from '@/components/Page.tsx';
import {Headline, Input, Textarea, Cell, Caption, Radio} from '@telegram-apps/telegram-ui';
import { useTranslation } from 'react-i18next';
import { useSecretSanta } from '@/providers/SecretSantaProvider.tsx';
import { useAuthUser } from '@/providers';
import { useAuthProfile } from '@/providers/AuthProfileProvider.tsx';
import { useNavigation } from '@/providers/NavigationProvider.tsx';
import {createWishlist, joinSecretSantaGame, updateParticipant} from '@/api/apiMethods.ts';
import { TbUser } from 'react-icons/tb';
import { mainButton } from '@telegram-apps/sdk-react';
import { setOnClickMainButton } from '@/helpers/tgButtons.ts';
import {useOutletContext} from "react-router-dom";
import {haptic} from "@/helpers/haptic.ts";

export const JoinSecretSantaPage: FC = () => {
  const { game } = useSecretSanta();
  const { authUser } = useAuthUser();
  const { authProfile } = useAuthProfile();
  const { t } = useTranslation();
  const { navigateToRoute, showSnackbar } = useNavigation();
  const { refreshProfile } = useOutletContext<{refreshProfile: () => Promise<void> | undefined}>();

  const myParticipant = game?.participants.find(p => p.user_id === authUser?.id);
  const isEditing = !!myParticipant;
  
  const [displayName, setDisplayName] = useState(myParticipant?.display_name || authUser?.fullname || '');
  const [selectedWishlistId, setSelectedWishlistId] = useState<string>(myParticipant?.wishlist_id || 'new');
  const [preferences, setPreferences] = useState(myParticipant?.preferences || '');
  const [isJoining, setIsJoining] = useState(false);

  if (!authProfile) {
    throw new Error('JoinSecretSantaPage must be inside the AuthProfileProvider');
  }

  const handleJoin = async () => {
    if (!game || !displayName || !selectedWishlistId) {
      haptic.notification('error');
      showSnackbar('❌', 'system.emptyTitle');
      return;
    }

    let wishlistId = selectedWishlistId;
    if (selectedWishlistId ==='new') {
      try {
        const newWishlist = await createWishlist({
          title: t('secretSanta.join.wishlist_for_santa'),
          cover: null,
          description: '',
          access: "link",
          wishIds: null,
        })
        wishlistId = newWishlist.id;
        refreshProfile()?.then().catch();
      } catch (error) {
        haptic.notification('error');
        showSnackbar('❌', 'system.unknownError');
        return;
      }
    }

    setIsJoining(true);
    try {
      const participantInfo = {
        display_name: displayName,
        wishlist_id: wishlistId,
        preferences: preferences || null,
      };

      if (isEditing) {
        await updateParticipant(game.id, participantInfo);
        haptic.notification('success');
        showSnackbar('✅', 'system.settingsSaved');
      } else {
        await joinSecretSantaGame(game.id, participantInfo);
        haptic.notification('success');
        showSnackbar('✅', 'secretSanta.participants.join');
      }
      
      navigateToRoute(`/main/secret-santa/${game.id}`, true);
    } catch (error) {
      haptic.notification('error');
      showSnackbar('❌', isEditing ? 'secretSanta.error.updateFailed' : 'secretSanta.error.joinFailed');
    } finally {
      setIsJoining(false);
    }
  };

  useEffect(() => {
    setOnClickMainButton(async () => {
      await handleJoin();
    });
    mainButton.setParams({
      isVisible: true,
      isLoaderVisible: isJoining,
      isEnabled: !isJoining && !!displayName && !!selectedWishlistId,
      text: isEditing ? t('action.save') : t('secretSanta.join.submit'),
    });
  }, [handleJoin, isJoining, displayName, selectedWishlistId]);

  if (!game) {
    return (
      <Page>
        <div className="p-10 text-center">
          <Caption className="text-color-hint">{t('secretSanta.error.loadFailed')}</Caption>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <div className="p-3 pb-10 flex flex-col gap-3">
        <Headline className="pl-2 text-center mb-4" weight="2">
          {isEditing ? t('secretSanta.participants.edit') : t('secretSanta.join.title')}
        </Headline>

        <Input
          placeholder={t('secretSanta.join.displayName')}
          before={<TbUser size={20} />}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />

        <div>
          <Caption className="pl-3 mb-2 text-color-hint">{t('secretSanta.join.selectWishlist')}</Caption>
          <Cell
            key={"wishlist_new"}
            Component="label"
            before={<Radio name="wishlist"
              value="new"
              checked={selectedWishlistId === "new"}
              onChange={(e) => {
                haptic.selection();
                setSelectedWishlistId(e.target.value);
              }}
            />}
          >
            <Caption className={"text-color-hint"} size={1}>({t('system.create') + " " + t('secretSanta.join.wishlist_for_santa')})</Caption>
          </Cell>
          {authProfile.wishlistPreviews.map((wishlist) => (
            <Cell
              key={wishlist.id}
              Component="label"
              before={
                <Radio name="wishlist"
                  value={wishlist.id}
                  checked={selectedWishlistId === wishlist.id}
                  onChange={(e) => {
                    haptic.selection();
                    setSelectedWishlistId(e.target.value);
                  }}
                />
              }
            >
              {wishlist.title}
            </Cell>
          ))}
        </div>

        <Textarea
          placeholder={t('secretSanta.join.preferences')}
          value={preferences}
          onChange={(e) => setPreferences(e.target.value)}
        />
      </div>
    </Page>
  );
};
